import { useState, useMemo, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Search, TrendingUp, TrendingDown, AlertTriangle, Activity, LayoutGrid, List } from 'lucide-react';
import { formatUSD, formatPercent } from '@/lib/format';
import { useCryptoAssets } from '@/hooks/useCryptoAssets';
import { AssetSummary, SortOption, FilterOption } from '@/types/crypto';
import { AssetDrawer } from '@/components/AssetDrawer';
import { SortFilter } from '@/components/SortFilter';
import { MarketTable } from '@/components/MarketTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkline } from '@/components/Sparkline';
import { cn } from '@/lib/utils';

interface CryptoAgentProps {
  initialAssetSymbol?: string;
}

type ViewMode = 'table' | 'cards';

const CryptoAgent = ({ initialAssetSymbol }: CryptoAgentProps = {}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('market_cap');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [selectedAsset, setSelectedAsset] = useState<AssetSummary | null>(null);
  const [autoAnalyze, setAutoAnalyze] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const { data: assets, isLoading, error } = useCryptoAssets();

  const handleCloseDrawer = () => {
    setSelectedAsset(null);
    setAutoAnalyze(false);
  };

  useEffect(() => {
    if (initialAssetSymbol && assets && !selectedAsset) {
      const asset = assets.find(a => 
        a.symbol.toLowerCase() === initialAssetSymbol.toLowerCase()
      );
      if (asset) {
        setSelectedAsset(asset);
      }
    }
  }, [initialAssetSymbol, assets, selectedAsset]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
        if (searchInput) searchInput.focus();
      }
      if (e.key === 'Escape' && selectedAsset) {
        setSelectedAsset(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedAsset]);

  // Market summary with counts
  const marketSummary = useMemo(() => {
    if (!assets?.length) return null;
    
    const gainers = assets.filter(asset => asset.change_24h > 0).length;
    const losers = assets.filter(asset => asset.change_24h < 0).length;
    const defi = assets.filter(asset => asset.tvl && asset.tvl > 0).length;
    const withAlerts = assets.filter(asset => asset.alerts && asset.alerts.length > 0).length;
    const totalVolume = assets.reduce((sum, asset) => sum + asset.volume_24h, 0);
    const totalMarketCap = assets.reduce((sum, asset) => sum + asset.market_cap, 0);
    
    const sentiment = gainers > losers ? 'bullish' : gainers < losers ? 'bearish' : 'neutral';
    
    return { 
      gainers, 
      losers, 
      defi,
      alerts: withAlerts,
      totalVolume, 
      totalMarketCap,
      total: assets.length,
      sentiment
    };
  }, [assets]);

  const filteredAndSortedAssets = useMemo(() => {
    if (!assets) return [];

    let filtered = assets.filter((asset: AssetSummary) => {
      const matchesSearch = (asset.name?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
                           (asset.symbol?.toLowerCase() ?? '').includes(searchQuery.toLowerCase());
      
      if (filterBy === 'gainers') return matchesSearch && asset.change_24h > 0;
      if (filterBy === 'losers') return matchesSearch && asset.change_24h < 0;
      if (filterBy === 'defi') return matchesSearch && asset.tvl && asset.tvl > 0;
      if (filterBy === 'alerts') return matchesSearch && asset.alerts && asset.alerts.length > 0;
      
      return matchesSearch;
    });

    return filtered.sort((a: AssetSummary, b: AssetSummary) => {
      switch (sortBy) {
        case 'change_24h':
          return b.change_24h - a.change_24h;
        case 'change_7d':
          return b.change_7d - a.change_7d;
        case 'volume_24h':
          return b.volume_24h - a.volume_24h;
        case 'tvl':
          return (b.tvl || 0) - (a.tvl || 0);
        default:
          return b.market_cap - a.market_cap;
      }
    });
  }, [assets, searchQuery, sortBy, filterBy]);

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <Card className="glass-card p-6">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Erro ao carregar dados</h2>
            <p className="text-muted-foreground">{error.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 md:p-6 space-y-5">
        
        {/* Compact Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight">Market Explorer</h1>
            
            {marketSummary && (
              <Badge 
                variant="outline"
                className={cn(
                  "text-xs px-2.5 py-1 font-medium",
                  marketSummary.sentiment === 'bullish' 
                    ? "bg-profit/10 text-profit border-profit/30" 
                    : marketSummary.sentiment === 'bearish'
                    ? "bg-loss/10 text-loss border-loss/30"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {marketSummary.sentiment === 'bullish' && <TrendingUp className="h-3 w-3 mr-1" />}
                {marketSummary.sentiment === 'bearish' && <TrendingDown className="h-3 w-3 mr-1" />}
                {marketSummary.sentiment === 'bullish' ? 'Bullish' : marketSummary.sentiment === 'bearish' ? 'Bearish' : 'Neutral'}
              </Badge>
            )}

            {assets && (
              <span className="text-sm text-muted-foreground">
                {assets.length} ativos
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 lg:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar... (/)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 bg-card/50 border-border/50 focus:border-primary/50"
              />
            </div>

            {/* View Toggle */}
            <div className="flex items-center border border-border/50 rounded-lg p-0.5 bg-card/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('table')}
                className={cn(
                  "h-7 px-2.5",
                  viewMode === 'table' ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                )}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('cards')}
                className={cn(
                  "h-7 px-2.5",
                  viewMode === 'cards' ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                )}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Stats Bar */}
        {marketSummary && (
          <div className="flex items-center gap-4 px-4 py-2.5 rounded-lg bg-card/30 border border-border/40 text-sm overflow-x-auto">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-muted-foreground">MCap Total</span>
              <span className="font-semibold">{formatUSD(marketSummary.totalMarketCap)}</span>
            </div>
            <div className="h-4 w-px bg-border/50" />
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-muted-foreground">Vol 24h</span>
              <span className="font-semibold">{formatUSD(marketSummary.totalVolume)}</span>
            </div>
            <div className="h-4 w-px bg-border/50" />
            <div className="flex items-center gap-3 whitespace-nowrap">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-profit" />
                <span className="font-semibold text-profit">{marketSummary.gainers}</span>
                <span className="text-muted-foreground">Alta</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingDown className="h-3.5 w-3.5 text-loss" />
                <span className="font-semibold text-loss">{marketSummary.losers}</span>
                <span className="text-muted-foreground">Baixa</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <SortFilter
          sortBy={sortBy}
          setSortBy={setSortBy}
          filterBy={filterBy}
          setFilterBy={setFilterBy}
          counts={marketSummary ? {
            all: marketSummary.total,
            gainers: marketSummary.gainers,
            losers: marketSummary.losers,
            defi: marketSummary.defi,
            alerts: marketSummary.alerts,
          } : undefined}
        />

        {/* Content */}
        <div className="fade-in-up">
          {isLoading ? (
            viewMode === 'table' ? (
              <div className="space-y-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-48 rounded-xl" />
                ))}
              </div>
            )
          ) : viewMode === 'table' ? (
            <MarketTable
              assets={filteredAndSortedAssets}
              onSelectAsset={setSelectedAsset}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {filteredAndSortedAssets.map((asset: AssetSummary) => (
                <Card 
                  key={asset.id} 
                  className="glass-card group hover-scale cursor-pointer transition-all duration-200 hover:border-primary/30"
                  onClick={() => setSelectedAsset(asset)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      {asset.image ? (
                        <img 
                          src={asset.image} 
                          alt={asset.name}
                          className="w-9 h-9 rounded-full"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary">
                            {asset.symbol.slice(0, 2)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold truncate">{asset.name}</CardTitle>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>#{asset.rank}</span>
                          <span>•</span>
                          <span className="font-medium">{asset.symbol}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-sm">{formatUSD(asset.price)}</div>
                        <div className={cn(
                          "text-xs font-medium",
                          asset.change_24h >= 0 ? "text-profit" : "text-loss"
                        )}>
                          {formatPercent(asset.change_24h)}
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 space-y-3">
                    {/* Sparkline */}
                    {asset.sparkline_7d && asset.sparkline_7d.length > 0 && (
                      <div className="py-1">
                        <Sparkline data={asset.sparkline_7d} width={240} height={40} />
                      </div>
                    )}

                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="text-muted-foreground">MCap</div>
                        <div className="font-medium">{formatUSD(asset.market_cap)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Vol 24h</div>
                        <div className="font-medium">{formatUSD(asset.volume_24h)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">7d</div>
                        <div className={cn(
                          "font-medium",
                          (asset.change_7d || 0) >= 0 ? "text-profit" : "text-loss"
                        )}>
                          {formatPercent(asset.change_7d)}
                        </div>
                      </div>
                    </div>

                    {asset.tvl && asset.tvl > 0 && (
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-border/30">
                        <span className="text-muted-foreground">TVL</span>
                        <span className="font-medium text-primary">{formatUSD(asset.tvl)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredAndSortedAssets.length === 0 && !isLoading && (
            <div className="text-center py-16">
              <div className="h-14 w-14 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                <Search className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Nenhum resultado</h3>
              <p className="text-sm text-muted-foreground">
                Tente ajustar os filtros ou o termo de busca.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Asset Drawer */}
      <AssetDrawer
        asset={selectedAsset}
        isOpen={!!selectedAsset}
        onClose={handleCloseDrawer}
        autoAnalyze={autoAnalyze}
      />
    </div>
  );
};

export default CryptoAgent;
